import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SUBJECTS = [
  "Book Orders",
  "Research Inquiry",
  "Media / Press",
  "Permissions",
  "General",
];

const INFO = [
  {
    icon: Mail,
    label: "Email the archive",
    value: "archive@devata-datasia.org",
    href: "mailto:archive@devata-datasia.org",
  },
  {
    icon: MapPin,
    label: "DatAsia Editions",
    value: "Siem Reap · Honolulu",
  },
  {
    icon: Clock,
    label: "Reply window",
    value: "Usually within 3 working days",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success(
        "Thank you — your message has reached the archive. We'll reply soon."
      );
      setForm({ name: "", email: "", subject: "", message: "" });
      setSubmitting(false);
    }, 600);
  };

  const field =
    "w-full rounded-md bg-[var(--bg)] border border-[var(--border-c)] px-4 py-3.5 font-sans text-sm text-[var(--text)] placeholder:text-[var(--text-2)]/60 focus:outline-none focus:border-[var(--gold)] transition-colors";

  return (
    <div data-testid="contact-page">
      {/* Masthead */}
      <section className="relative pt-36 lg:pt-44 pb-16 lg:pb-24 bg-[var(--surface)] border-b border-[var(--border-c)] overflow-hidden">
        <div className="pointer-events-none absolute -right-20 -top-20 w-[32rem] h-[32rem] rounded-full bg-[var(--gold)]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 bottom-0 w-[22rem] h-[22rem] rounded-full bg-[var(--terracotta)]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--gold)]"
            >
              Correspondence · Chapter · 2026
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.08 }}
              className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight text-[var(--text)]"
            >
              Write to the <span className="italic text-[var(--terracotta)]">archive.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.14 }}
              className="mt-6 font-sans text-base sm:text-lg leading-relaxed text-[var(--text-2)] max-w-xl"
            >
              Research questions, book orders, press requests, image permissions
              — real humans read every letter. Tell us what you are looking for.
            </motion.p>
          </div>
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-hand text-3xl text-[var(--terracotta)] -rotate-2 inline-block"
            >
              we'd love to hear from you —
            </motion.div>
          </div>
        </div>
      </section>

      {/* Form + info */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* INFO column */}
          <Reveal className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--text-2)]">
              Direct lines
            </div>
            <ul className="mt-6 space-y-6">
              {INFO.map((row) => {
                const Row = row.href ? "a" : "div";
                return (
                  <Row
                    key={row.label}
                    {...(row.href ? { href: row.href } : {})}
                    className="group flex items-start gap-4 pb-6 border-b border-[var(--border-c)] last:border-b-0"
                  >
                    <span className="shrink-0 h-11 w-11 grid place-items-center rounded-full border border-[var(--border-c)] text-[var(--text)] group-hover:bg-[var(--gold)] group-hover:border-[var(--gold)] group-hover:text-[var(--ink)] transition-colors">
                      <row.icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-2)]">
                        {row.label}
                      </div>
                      <div className="mt-1 font-serif text-xl text-[var(--text)]">
                        {row.value}
                      </div>
                    </div>
                  </Row>
                );
              })}
            </ul>

            <div className="mt-10 p-6 bg-[var(--surface)] border border-[var(--border-c)] rounded-sm">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--gold)]">
                A note on orders
              </div>
              <p className="mt-3 font-sans text-sm leading-relaxed text-[var(--text-2)]">
                All DatAsia titles ship worldwide via Amazon — the fastest route
                for most readers. For bulk, academic, or signed orders, contact
                us directly here.
              </p>
            </div>
          </Reveal>

          {/* FORM column */}
          <Reveal delay={0.1} className="lg:col-span-8">
            <form
              onSubmit={submit}
              className="relative bg-[var(--surface)] border border-[var(--border-c)] rounded-sm p-7 sm:p-10 deckle"
            >
              <div className="flex items-baseline justify-between pb-6 border-b border-[var(--border-c)]">
                <h2 className="font-serif text-2xl sm:text-3xl text-[var(--text)]">
                  Send a letter
                </h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--text-2)]">
                  Form · 01
                </span>
              </div>

              <div className="mt-7 grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-2)] mb-2">
                    Name
                  </label>
                  <input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    data-testid="contact-name-input"
                    className={field}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-2)] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    data-testid="contact-email-input"
                    className={field}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-2)] mb-2">
                  Subject
                </label>
                <Select value={form.subject} onValueChange={update("subject")}>
                  <SelectTrigger
                    data-testid="contact-subject-select"
                    className={`${field} justify-between`}
                  >
                    <SelectValue placeholder="Choose a topic" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg)] border-[var(--border-c)]">
                    {SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s} className="text-[var(--text)]">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5">
                <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-2)] mb-2">
                  Message
                </label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us a little about your inquiry…"
                  data-testid="contact-message-input"
                  className={`${field} resize-none`}
                />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="font-sans text-xs text-[var(--text-2)] max-w-md">
                  By sending, you agree we may reply to the email you provided.
                  We never share your details.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[var(--ink)] text-[var(--bg)] px-8 py-3.5 font-sans text-sm font-semibold hover:bg-[var(--terracotta)] transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
